import { Box, Typography } from "@mui/material";
import { keyframes, styled, type Theme } from "@mui/material/styles";
import { axisClasses } from "@mui/x-charts/ChartsAxis";
import { chartsAxisHighlightClasses } from "@mui/x-charts/ChartsAxisHighlight";
import { LineChart, lineClasses } from "@mui/x-charts/LineChart";

import { PLOT_PADDING_Y, Y_AXIS_WIDTH } from "~/feature/chart/constants";

import { ChartLineVariantEnum, type IChartLine } from "./buildChartLines";
import {
  CHART_HEIGHT,
  CONSENSUS_LINE_WIDTH,
  CURSOR_HINT_FONT_SIZE,
  CURSOR_HINT_TOP,
  DEFAULT_LINE_WIDTH,
  DOTTED_LINE_DASH,
  DOTTED_LINE_WIDTH,
  LINE_PAST_CLASS,
  NO_DATA_FONT_SIZE,
  NO_LINE_DASH,
  PAST_LINE_EXTRA_WIDTH,
  TICK_FONT_SIZE,
  WATERMARK_MAX_WIDTH,
  WATERMARK_OPACITY,
  WATERMARK_TOP,
  WATERMARK_WIDTH,
} from "./constants";

export type ChartLineStyle = Pick<IChartLine, "key" | "variant">;

export const Root = styled(Box)({
  position: "relative",
  width: "100%",
});

export const ChartArea = styled(Box)({
  position: "relative",
  width: "100%",
  height: CHART_HEIGHT,
});

export const Watermark = styled(Box)({
  position: "absolute",
  top: WATERMARK_TOP,
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: WATERMARK_WIDTH,
  maxWidth: WATERMARK_MAX_WIDTH,
  opacity: WATERMARK_OPACITY,
  pointerEvents: "none",
});

export const watermarkSvgStyle = { width: "100%", height: "auto" };

const strokeByVariant: Record<
  ChartLineVariantEnum,
  Record<string, string | number> | null
> = {
  [ChartLineVariantEnum.SOLID]: null,
  [ChartLineVariantEnum.DASHED]: { strokeDasharray: NO_LINE_DASH },
  [ChartLineVariantEnum.DOTTED]: {
    strokeDasharray: DOTTED_LINE_DASH,
    strokeWidth: DOTTED_LINE_WIDTH,
  },
  [ChartLineVariantEnum.CONSENSUS]: { strokeWidth: CONSENSUS_LINE_WIDTH },
};

const widthByVariant: Record<ChartLineVariantEnum, number> = {
  [ChartLineVariantEnum.SOLID]: DEFAULT_LINE_WIDTH,
  [ChartLineVariantEnum.DASHED]: DEFAULT_LINE_WIDTH,
  [ChartLineVariantEnum.DOTTED]: DOTTED_LINE_WIDTH,
  [ChartLineVariantEnum.CONSENSUS]: CONSENSUS_LINE_WIDTH,
};

const styledLines = ($lineStyles: ChartLineStyle[]) =>
  Object.fromEntries(
    $lineStyles.flatMap(({ key, variant }) => {
      const stroke = strokeByVariant[variant];
      // v9: per-series classes (`MuiLineElement-series-<id>`) were replaced by a
      // `data-series` attribute on the line path (class `lineClasses.line`).
      const seriesSelector = `.${lineClasses.line}[data-series="${key}"]`;

      return [
        ...(stroke ? [[`& ${seriesSelector}`, stroke]] : []),
        [
          `& .${LINE_PAST_CLASS} ${seriesSelector}`,
          { strokeWidth: widthByVariant[variant] + PAST_LINE_EXTRA_WIDTH },
        ],
      ];
    })
  );

export const Chart = styled(LineChart, {
  shouldForwardProp: (prop) => prop !== "$showYAxis" && prop !== "$lineStyles",
})<{ $showYAxis: boolean; $lineStyles: ChartLineStyle[] }>(
  ({ $showYAxis, $lineStyles, theme }) => ({
    width: "100%",

    [`& .${axisClasses.directionY} .${axisClasses.tickLabel}`]: {
      fontSize: TICK_FONT_SIZE,
      fill: theme.palette.text.disabled,
      display: $showYAxis ? undefined : "none",
    },

    [`& .${chartsAxisHighlightClasses.root}`]: {
      stroke: theme.palette.grey[400],
      strokeDasharray: "none",
    },

    ...styledLines($lineStyles),
  })
);

export const CursorHint = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$leftPercent",
})<{ $leftPercent: number }>(({ $leftPercent, theme }) => ({
  position: "absolute",
  top: CURSOR_HINT_TOP,
  left: `calc(${Y_AXIS_WIDTH}px + (100% - ${Y_AXIS_WIDTH}px) * ${$leftPercent / 100})`,
  transform: "translateX(-50%)",
  fontSize: CURSOR_HINT_FONT_SIZE,
  lineHeight: 1,
  color: theme.palette.text.secondary,
  whiteSpace: "nowrap",
  pointerEvents: "none",
  padding: theme.spacing(0.25, 0.75),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.grey[100],
}));

const skeletonPulse = keyframes({
  "0%": { opacity: 1 },
  "50%": { opacity: 0.4 },
  "100%": { opacity: 1 },
});

export const SkeletonPlot = styled("svg")({
  position: "absolute",
  top: PLOT_PADDING_Y,
  bottom: PLOT_PADDING_Y,
  left: Y_AXIS_WIDTH,
  right: 0,
  width: `calc(100% - ${Y_AXIS_WIDTH}px)`,
  height: `calc(100% - ${2 * PLOT_PADDING_Y}px)`,
  animation: `${skeletonPulse} 2s ease-in-out 0.5s infinite`,
});

export const SkeletonLine = styled("path")(({ theme }) => ({
  fill: "none",
  stroke: theme.palette.grey[300],
  strokeWidth: 2,
  vectorEffect: "non-scaling-stroke",
  strokeLinejoin: "round",
}));

export const noDataOverlaySx = (theme: Theme) => ({
  fontSize: NO_DATA_FONT_SIZE,
  fill: theme.palette.text.disabled,
});
